import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from './ui/button';
import { ChevronRight, Sparkles } from 'lucide-react';

const GROUP_TITLES = {
  essentials: 'Essentials',
  direction: 'Direction',
  details: 'Details',
};

const GROUP_ORDER = ['essentials', 'direction', 'details'];

const DECIDE_FOR_ME_VALUE = '__decide_for_me__';
const OTHER_VALUE = '__other__';

// Compact wrapping pill — the redesign's core density win. Option
// descriptions live in the tooltip; the selected chip shows its desc inline
// below the row when present.
function Chip({ active, onClick, children, title, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      className={
        'rounded-full border px-3 py-1.5 text-[13px] transition whitespace-nowrap ' +
        (active
          ? 'border-accent bg-accent text-accent-foreground font-semibold'
          : 'border-border bg-card text-foreground/75 hover:border-accent/50 hover:text-foreground') +
        (disabled ? ' opacity-50 cursor-not-allowed' : '')
      }
    >
      {children}
    </button>
  );
}

function SingleSelect({ question, value, onChange }) {
  const [otherText, setOtherText] = useState(
    typeof value === 'string' && value.startsWith('other:') ? value.slice(6) : ''
  );
  const isDecide = value === DECIDE_FOR_ME_VALUE;
  const isOther = typeof value === 'string' && value.startsWith('other:');
  const selectedOpt = (question.options || []).find(o => o.value === value);

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {(question.options || []).map(opt => opt.swatches ? (
          // Theme-direction card (rounds 70-71): a MINI SLIDE rendered in the
          // theme's real colors and fonts — background, accent bar, heading
          // specimen, surface card — so minimal vs editorial vs bold is
          // visible at a glance, not inferred from color dots.
          (() => {
            const [bg, surface, accent, accent2, text] = opt.swatches;
            const [headFont, bodyFont] = String(opt.fonts || '').split('/').map(s => s.trim());
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                className={
                  'text-left rounded-lg border overflow-hidden transition-all w-[210px] ' +
                  (value === opt.value
                    ? 'border-accent ring-2 ring-accent shadow-md'
                    : 'border-border bg-card hover:border-accent/50 hover:shadow-sm')
                }
              >
                <span className="block relative h-[96px] p-2.5" style={{ backgroundColor: bg }}>
                  <span className="block w-6 h-[3px] rounded-full mb-1.5" style={{ backgroundColor: accent }} />
                  <span
                    className="block text-[13px] leading-[1.15] font-bold"
                    style={{ color: text, fontFamily: headFont ? `'${headFont}', sans-serif` : undefined }}
                  >
                    {opt.label}
                  </span>
                  <span
                    className="block text-[8.5px] mt-1 leading-snug"
                    style={{ color: text, opacity: 0.75, fontFamily: bodyFont ? `'${bodyFont}', sans-serif` : undefined }}
                  >
                    Body copy sample — the quick brown fox.
                  </span>
                  <span className="absolute bottom-2 left-2.5 h-[18px] rounded" style={{ backgroundColor: surface, width: '58%' }} />
                  <span className="absolute bottom-2 right-2.5 w-[18px] h-[18px] rounded-full" style={{ backgroundColor: accent2 }} />
                </span>
                <span className="block px-2.5 py-1.5">
                  <span className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-semibold">{opt.label}</span>
                    {opt.recommended && (
                      <span className="rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-500/15 dark:text-emerald-400">
                        ★ Recommended
                      </span>
                    )}
                    {opt.style && (
                      <span className="rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-accent bg-accent/10">
                        {opt.style}
                      </span>
                    )}
                  </span>
                  {opt.desc && <span className="block text-[10.5px] text-muted-foreground">{opt.desc}</span>}
                  {opt.fonts && <span className="block text-[9.5px] text-muted-foreground/80 mt-0.5">{opt.fonts}</span>}
                </span>
              </button>
            );
          })()
        ) : (
          <Chip
            key={opt.value}
            active={value === opt.value}
            title={opt.desc || undefined}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </Chip>
        ))}
        {question.allow_decide_for_me && (
          <Chip active={isDecide} onClick={() => onChange(DECIDE_FOR_ME_VALUE)} title="Let Wizardly pick">
            ✦ Decide for me
          </Chip>
        )}
        {question.allow_other && (
          <span
            className={
              'inline-flex items-center rounded-full border px-3 py-1 ' +
              (isOther ? 'border-accent bg-accent/10' : 'border-border bg-card')
            }
          >
            <input
              type="text"
              value={otherText}
              placeholder="Other…"
              onChange={e => {
                setOtherText(e.target.value);
                onChange(e.target.value ? `other:${e.target.value}` : '');
              }}
              className="w-24 bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground/70 focus:w-40 transition-all"
            />
          </span>
        )}
      </div>
      {selectedOpt?.desc && (
        <p className="mt-1.5 text-[11.5px] text-muted-foreground">{selectedOpt.desc}</p>
      )}
    </div>
  );
}

function MultiSelect({ question, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = v => {
    if (selected.includes(v)) onChange(selected.filter(x => x !== v));
    else onChange([...selected, v]);
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {(question.options || []).map(opt => (
        <Chip
          key={opt.value}
          active={selected.includes(opt.value)}
          title={opt.desc || undefined}
          onClick={() => toggle(opt.value)}
        >
          {opt.label}
        </Chip>
      ))}
      {question.allow_decide_for_me && (
        <Chip
          active={selected.includes(DECIDE_FOR_ME_VALUE)}
          onClick={() => toggle(DECIDE_FOR_ME_VALUE)}
          title="Let Wizardly pick"
        >
          ✦ Decide for me
        </Chip>
      )}
    </div>
  );
}

function SliderQuestion({ question, value, onChange }) {
  const min = question.min ?? 1;
  const max = question.max ?? 10;
  const def = question.default ?? Math.round((min + max) / 2);
  const v = typeof value === 'number' ? value : def;
  const pct = ((v - min) / (max - min)) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{min} {question.min_label ? `· ${question.min_label}` : ''}</span>
        <span className="font-mono text-foreground">{v}</span>
        <span>{max} {question.max_label ? `· ${question.max_label}` : ''}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={v}
        onChange={e => onChange(parseInt(e.target.value, 10))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer outline-none focus:ring-2 focus:ring-accent/40 clarifier-range"
        style={{
          background: `linear-gradient(to right, hsl(var(--accent)) 0%, hsl(var(--accent)) ${pct}%, hsl(var(--border)) ${pct}%, hsl(var(--border)) 100%)`,
        }}
      />
      <style>{`
        .clarifier-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 9999px;
          background: hsl(var(--card));
          border: 2px solid hsl(var(--accent));
          cursor: pointer;
        }
        .clarifier-range::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 9999px;
          background: hsl(var(--card));
          border: 2px solid hsl(var(--accent));
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}

function FreetextQuestion({ value, onChange, placeholder }) {
  return (
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || 'Anything else? References, anti-patterns, hard requirements.'}
      rows={2}
      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-accent/50 focus:outline-none focus:ring-1 focus:ring-accent/30"
    />
  );
}

function QuestionSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="animate-pulse">
        <div className="mb-3 h-3.5 w-1/3 rounded bg-muted" />
        <div className="flex gap-2">
          <div className="h-7 w-24 rounded-full bg-muted" />
          <div className="h-7 w-28 rounded-full bg-muted" />
          <div className="h-7 w-20 rounded-full bg-muted" />
        </div>
      </div>
    </div>
  );
}

function QuestionBlock({ q, value, isAuto, onChange }) {
  return (
    <div className="py-3 first:pt-0 last:pb-0" style={{ animation: 'clarifierQuestionIn 0.32s ease-out both' }}>
      <div className="mb-1.5 flex items-center gap-2">
        <h4 className="text-[13px] font-semibold text-foreground">{q.label}</h4>
        {isAuto && (
          <span className="rounded px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-teal-600 bg-teal-500/10">
            auto
          </span>
        )}
        {!isAuto && value != null && value !== '' && q.type === 'single-select' && (
          <span className="rounded px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-accent bg-accent/10">
            you
          </span>
        )}
        {q.required && q.type !== 'freetext'
          && (value == null || value === '' || (Array.isArray(value) && value.length === 0)) && (
          <span className="text-[10px] text-destructive">required</span>
        )}
      </div>
      {q.sub_help && <p className="mb-2 text-[11.5px] text-muted-foreground">{q.sub_help}</p>}
      {q.type === 'single-select' && <SingleSelect question={q} value={value} onChange={onChange} />}
      {q.type === 'multi-select' && <MultiSelect question={q} value={value} onChange={onChange} />}
      {q.type === 'slider' && <SliderQuestion question={q} value={value} onChange={onChange} />}
      {q.type === 'freetext' && <FreetextQuestion value={value} onChange={onChange} placeholder={q.sub_help} />}
      {isAuto && q.why && (
        <p className="mt-1.5 text-[11.5px] text-muted-foreground">
          <Sparkles className="mr-1 inline h-3 w-3 text-teal-600" aria-hidden="true" />
          {q.why}
        </p>
      )}
    </div>
  );
}

export default function ClarifyingQuestionsPanel({
  questions = [],
  initialAnswers = {},
  onSubmit,
  onCancel,
  rationale = '',
  loading = false,
  streaming = false,
}) {
  const [answers, setAnswers] = useState(initialAnswers);
  // Questions whose CURRENT answer is the clarifier's own suggestion,
  // untouched by the user. Drives the auto/you badges and the CTA count.
  const [autoIds, setAutoIds] = useState(() => new Set());
  const [detailsOpen, setDetailsOpen] = useState(false);

  const setAnswer = useCallback((id, val) => {
    setAnswers(prev => ({ ...prev, [id]: val }));
    setAutoIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Seed answers as questions stream in:
  //  - sliders show their default, so it IS the answer (pre-existing rule);
  //  - single-selects with a valid `suggested` pre-select it (the redesign:
  //    the user reviews decisions instead of answering questions).
  // initialAnswers always win, a question is only ever seeded ONCE (a user
  // who deliberately clears an answer must not have the suggestion re-applied
  // when the next streamed question re-runs this effect), and the seed list
  // is computed OUTSIDE the setState updater — updaters must stay pure.
  const answersRef = React.useRef(answers);
  useEffect(() => { answersRef.current = answers; }, [answers]);
  const seededOnceRef = React.useRef(new Set());
  useEffect(() => {
    const cur = answersRef.current;
    const seeds = {};
    const seedAutoIds = [];
    for (const q of questions) {
      if (q.type === 'slider' && typeof cur[q.id] !== 'number' && !(q.id in seeds)) {
        const min = q.min ?? 1;
        const max = q.max ?? 10;
        seeds[q.id] = q.default ?? Math.round((min + max) / 2);
      }
      if (q.type === 'single-select' && (cur[q.id] == null || cur[q.id] === '')
          && q.suggested != null && !seededOnceRef.current.has(q.id)) {
        const valid = (q.options || []).some(o => o.value === q.suggested);
        if (valid) {
          seeds[q.id] = q.suggested;
          seedAutoIds.push(q.id);
          seededOnceRef.current.add(q.id);
        }
      }
    }
    if (Object.keys(seeds).length) {
      setAnswers(prev => {
        let changed = false;
        const next = { ...prev };
        for (const [id, v] of Object.entries(seeds)) {
          const existing = next[id];
          const empty = typeof v === 'number'
            ? typeof existing !== 'number'
            : (existing == null || existing === '');
          if (empty) {
            next[id] = v;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }
    if (seedAutoIds.length) {
      setAutoIds(prev => {
        const next = new Set(prev);
        seedAutoIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [questions]);

  // A real brand selected IN THIS PANEL locks the visual direction: the
  // theme question disappears (the brand IS the theme) and any auto-reason
  // that cites a brand is dropped when NO brand is selected (stale rationale
  // from the question-generation snapshot — round 71 user report).
  const brandAnswer = answers['brand_identity'];
  const brandLocked = !!brandAnswer && brandAnswer !== 'none'
    && brandAnswer !== DECIDE_FOR_ME_VALUE && !String(brandAnswer).startsWith('other:');

  const grouped = useMemo(() => {
    const buckets = { essentials: [], direction: [], details: [] };
    for (const q of questions) {
      if (q.id === 'theme_direction' && brandLocked) continue;
      const qq = (!brandLocked && q.why && /brand identity/i.test(q.why))
        ? { ...q, why: null } : q;
      const g = buckets[qq.group] ? qq.group : 'details';
      buckets[g].push(qq);
    }
    return buckets;
  }, [questions, brandLocked]);

  // Load the proposed theme fonts so the mini-slide previews render in the
  // REAL faces (a font-name label can't show minimal vs editorial).
  useEffect(() => {
    const fams = new Set();
    for (const q of questions) {
      if (q.id !== 'theme_direction') continue;
      for (const o of (q.options || [])) {
        String(o.fonts || '').split('/').forEach(f => { const t = f.trim(); if (t) fams.add(t); });
      }
    }
    if (!fams.size) return;
    const href = 'https://fonts.googleapis.com/css2?'
      + [...fams].map(f => 'family=' + encodeURIComponent(f).replace(/%20/g, '+') + ':wght@400;700').join('&')
      + '&display=swap';
    if (![...document.querySelectorAll('link[data-clarifier-fonts]')].some(l => l.href === href)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-clarifier-fonts', '1');
      document.head.appendChild(link);
    }
  }, [questions]);

  const autoCount = useMemo(() => {
    const ids = new Set(questions.map(q => q.id));
    let n = 0;
    autoIds.forEach(id => { if (ids.has(id)) n++; });
    return n;
  }, [autoIds, questions]);

  const canContinue = useMemo(() => {
    for (const q of questions) {
      if (!q.required) continue;
      // Freetext never gates Continue — an optional elaboration box marked
      // "required" by the LLM would otherwise silently block the flow
      // (possibly below the fold).
      if (q.type === 'freetext') continue;
      const a = answers[q.id];
      if (q.type === 'multi-select') {
        if (!Array.isArray(a) || a.length === 0) return false;
      } else if (a == null || a === '') {
        return false;
      }
    }
    return true;
  }, [questions, answers]);

  // A required-and-unanswered question hiding in the collapsed Details
  // section must force it open — a blocked Generate with no visible reason
  // is the worst outcome. Fires at most ONCE so the user can still collapse
  // the section afterward (Generate stays visibly blocked either way).
  const forcedOpenRef = React.useRef(false);
  useEffect(() => {
    if (detailsOpen || forcedOpenRef.current) return;
    const blocked = grouped.details.some(q => {
      if (!q.required || q.type === 'freetext') return false;
      // A question about to be auto-seeded doesn't need the section opened.
      if (q.type === 'single-select' && q.suggested != null
          && (q.options || []).some(o => o.value === q.suggested)) return false;
      const a = answers[q.id];
      if (q.type === 'multi-select') return !Array.isArray(a) || a.length === 0;
      return a == null || a === '';
    });
    if (blocked) {
      forcedOpenRef.current = true;
      setDetailsOpen(true);
    }
  }, [grouped, answers, detailsOpen]);

  const handleSubmit = () => {
    if (!canContinue || loading) return;
    onSubmit?.(answers);
  };

  if (!questions.length && !streaming) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground">
        No clarifying questions needed. Ready to generate.
      </div>
    );
  }

  const detailsCount = grouped.details.length;

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="border-b border-border bg-card px-6 py-4">
        <h2 className="text-lg font-semibold text-foreground">Quick check before we design</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {questions.length} decision{questions.length === 1 ? '' : 's'}
          {autoCount > 0 && (
            <span className="ml-1.5 rounded-full bg-teal-500/10 px-2 py-px text-[11px] font-semibold text-teal-600">
              ✦ {autoCount} pre-answered
            </span>
          )}
          {rationale && <span className="ml-2">{rationale}</span>}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 w-full">
        {streaming && questions.length === 0 && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Thinking through what to ask…</p>
            <QuestionSkeleton />
            <QuestionSkeleton />
          </div>
        )}

        <div className="rounded-xl border border-border bg-card">
          {GROUP_ORDER.filter(g => g !== 'details').map(groupKey => {
            const qs = grouped[groupKey];
            if (!qs || qs.length === 0) return null;
            return (
              <section key={groupKey} className="border-b border-border px-4 py-3 last:border-b-0">
                <h3 className="mb-2 text-[10.5px] font-bold uppercase tracking-widest text-muted-foreground/70">
                  {GROUP_TITLES[groupKey]}
                </h3>
                <div className="divide-y divide-border/60">
                  {qs.map(q => (
                    <QuestionBlock
                      key={q.id}
                      q={q}
                      value={answers[q.id]}
                      isAuto={autoIds.has(q.id)}
                      onChange={v => setAnswer(q.id, v)}
                    />
                  ))}
                </div>
              </section>
            );
          })}

          {detailsCount > 0 && (
            <section className="px-4 py-1">
              <button
                type="button"
                onClick={() => setDetailsOpen(o => !o)}
                aria-expanded={detailsOpen}
                className="flex w-full items-center gap-2 py-2 text-left text-[12.5px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${detailsOpen ? 'rotate-90' : ''}`} />
                {detailsCount} fine-tuning decision{detailsCount === 1 ? '' : 's'}
              </button>
              {detailsOpen && (
                <div className="divide-y divide-border/60 pb-2">
                  {grouped.details.map(q => (
                    <QuestionBlock
                      key={q.id}
                      q={q}
                      value={answers[q.id]}
                      isAuto={autoIds.has(q.id)}
                      onChange={v => setAnswer(q.id, v)}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>

        {streaming && questions.length > 0 && (
          <div className="mt-3 space-y-2">
            <QuestionSkeleton />
            <p className="text-xs text-muted-foreground">Loading more questions…</p>
          </div>
        )}
        <style>{`
          @keyframes clarifierQuestionIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>

      <div className="border-t border-border bg-card px-6 py-3">
        <div className="flex items-center justify-between gap-3">
          {onCancel ? (
            <Button variant="ghost" onClick={onCancel} disabled={loading}>Cancel</Button>
          ) : <span />}
          <Button onClick={handleSubmit} disabled={!canContinue || loading || streaming}>
            {loading
              ? 'Generating…'
              : streaming
                ? 'Loading questions…'
                : autoCount > 0
                  ? `Generate — ${autoCount} decided for you`
                  : 'Generate'}
          </Button>
        </div>
        {!streaming && !loading && (
          <p className="mt-1.5 text-right text-[11px] text-muted-foreground/70">
            Every answer stays editable until you generate
          </p>
        )}
      </div>
    </div>
  );
}

export { DECIDE_FOR_ME_VALUE, OTHER_VALUE };
